import { defineMessages } from '@atlassian/jira-intl';
import type { Metric } from './EntityKey.types.tsx';

const messages = defineMessages({
	name: {
		id: 'admin-pages-performance.common.assets.project-role-actor.name',
		defaultMessage: 'Project role permissions',
		description:
			'Name of the site optimizer metric that will be used as the title on the page and menu item.',
	},

	description: {
		id: 'admin-pages-performance.common.assets.project-role-actor.description',
		defaultMessage:
			"Here's a quick snapshot of the total number of project role permissions across your site.",
		description: 'Description of the site optimizer metric.',
	},

	analysis: {
		id: 'admin-pages-performance.common.assets.project-role-actor.analysis',
		defaultMessage: 'Analysis',
		description: 'Heading for analysis tab',
	},

	activityLog: {
		id: 'admin-pages-performance.common.assets.project-role-actor.activity-log',
		defaultMessage: 'Activity log',
		description: 'Heading for activity log',
	},

	warning: {
		id: 'admin-pages-performance.common.assets.project-role-actor.warning',
		defaultMessage: 'Your number of project role memberships is nearing the recommended threshold.',
		description: 'Warning message if metric are getting close to the threshold.',
	},

	exceeded: {
		id: 'admin-pages-performance.common.assets.project-role-actor.exceeded',
		defaultMessage: 'Your number of project role memberships is over the recommended threshold.',
		description: 'Message to show to the user if metric has exceeded the threshold.',
	},

	risk1: {
		id: 'admin-pages-performance.common.assets.project-role-actor.risk-1',
		defaultMessage:
			'When the number of project role memberships (users or groups) increases, viewing, searching, and creating issues becomes slower.',
		description: 'One of the risks when this metric exceeded the threshold.',
	},

	mitigation1: {
		id: 'admin-pages-performance.common.assets.project-role-actor.mitigation-1',
		defaultMessage:
			'Remove users who no longer need access to your site rather than only suspending them. Suspending users removes their permission but not their project role membership.',
		description: 'One of the mitigation options when this metric exceeded the threshold.',
	},

	mitigation2: {
		id: 'admin-pages-performance.common.assets.project-role-actor.mitigation-2',
		defaultMessage:
			'Give memberships to groups of users instead of individual users. Granting membership to a group of 50 users count as 1 membership whereas granting memberships to 50 single users counts as 50 memberships.',
		description: 'One of the mitigation options when this metric exceeded the threshold.',
	},

	mitigation3: {
		id: 'admin-pages-performance.common.assets.project-role-actor.mitigation-3',
		defaultMessage: 'Use the default project role members in your permission scheme.',
		description: 'One of the mitigation options when this metric exceeded the threshold.',
	},

	currentValueLabel: {
		id: 'admin-pages-performance.common.assets.project-role-actor.current-value-label',
		defaultMessage: 'Current project role permissions in your site',
		description: 'Label to be shown next to the current value of the metric.',
	},

	thresholdValueLabel: {
		id: 'admin-pages-performance.common.assets.project-role-actor.threshold-value-label',
		defaultMessage: 'Recommended maximum number of permissions',
		description: 'Label to be shown next to the threshold value of the metric.',
	},

	currentValueAriaLabel: {
		id: 'admin-pages-performance.common.assets.project-role-actor.current-value-aria-label',
		defaultMessage: '{number} current project role permissions in your site',
		description: 'Aria Label for the current value',
	},

	thresholdValueAriaLabel: {
		id: 'admin-pages-performance.common.assets.project-role-actor.threshold-value-aria-label',
		defaultMessage: '{number} recommended maximum number of permissions',
		description: 'Aria Label for the threshold value',
	},

	recommendationUrlText: {
		id: 'admin-pages-performance.common.assets.project-role-actor.recommendation-url-text',
		defaultMessage: 'Learn more about optimizing your project role memberships.',
		description: 'Text to be shown on the hypertext link to additional documentation.',
	},

	graphHeader: {
		id: 'admin-pages-performance.common.assets.project-role-actor.graph-header',
		defaultMessage: 'Project role permissions over time',
		description: 'Text to be shown on the header of the metric graph.',
	},
});

export const PROJECT_ROLE_ACTOR_KEY = 'projectroleactor';

export const projectRoleActorConstants: Metric = {
	key: PROJECT_ROLE_ACTOR_KEY,
	visible: true,
	name: messages.name,
	description: messages.description,
	tabLabels: [messages.analysis, messages.activityLog],
	warningMessage: messages.warning,
	exceededMessage: messages.exceeded,
	risks: [messages.risk1],
	mitigations: [messages.mitigation1, messages.mitigation2, messages.mitigation3],
	currentValueLabel: messages.currentValueLabel,
	currentValueAriaLabel: messages.currentValueAriaLabel,
	thresholdValueLabel: messages.thresholdValueLabel,
	thresholdValueAriaLabel: messages.thresholdValueAriaLabel,
	recommendationUrl:
		'https://support.atlassian.com/jira-cloud-administration/docs/optimize-your-number-of-project-role-memberships/',
	recommendationUrlText: messages.recommendationUrlText,
	graphHeader: messages.graphHeader,
};
